import type { Lead } from "../../types.ts";

export type LeadCentralCampaign = {
  label: string;
  name: string;
  slug: string;
};

const CENTRAL_CAMPAIGN_LABELS = [
  "Campanha Lead Central",
  "Lead Central Campaign",
  "Central Campaign",
  "Campaign Lead Central",
];

function textValue(value: unknown): string {
  return String(value || "").trim();
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function noteLineValue(notes: unknown, labels: string[]): string {
  const wanted = new Set(labels.map((label) => label.toLowerCase()));
  for (const line of textValue(notes).split(/\r?\n/)) {
    const separator = line.indexOf(":");
    if (separator <= 0) continue;
    const label = line.slice(0, separator).trim().toLowerCase();
    if (!wanted.has(label)) continue;
    const value = line.slice(separator + 1).trim();
    if (value) return value;
  }
  return "";
}

function formatCampaignLabel(name: string, slug: string): string {
  if (name && slug && name !== slug) return `${name} (${slug})`;
  return name || slug;
}

function campaignFromLabel(label: string): LeadCentralCampaign | null {
  const cleanLabel = textValue(label);
  if (!cleanLabel) return null;
  const match = cleanLabel.match(/^(.*?)\s*\(([^()]+)\)\s*$/);
  if (!match) return { label: cleanLabel, name: cleanLabel, slug: "" };
  const name = textValue(match[1]);
  const slug = textValue(match[2]);
  return { label: formatCampaignLabel(name, slug), name, slug };
}

export function getLeadCentralCampaign(lead: Lead | null | undefined): LeadCentralCampaign | null {
  if (!lead) return null;
  const centralCampaign = objectValue(lead.centralCampaign);
  const structuredName = textValue(
    centralCampaign.name
    || centralCampaign.title
    || lead.centralCampaignName
    || lead.leadCentralCampaignName,
  );
  const structuredSlug = textValue(
    centralCampaign.slug
    || lead.centralCampaignSlug
    || lead.leadCentralCampaignSlug,
  );
  if (structuredName || structuredSlug) {
    return {
      label: formatCampaignLabel(structuredName, structuredSlug),
      name: structuredName,
      slug: structuredSlug,
    };
  }

  const campaignLine = noteLineValue(lead.notes, CENTRAL_CAMPAIGN_LABELS);
  const fromLabel = campaignFromLabel(campaignLine);
  if (fromLabel) return fromLabel;

  const noteName = noteLineValue(lead.notes, ["Campaign Name", "Nome Campanha Lead Central"]);
  const noteSlug = noteLineValue(lead.notes, ["Campaign Slug", "Slug Campanha Lead Central"]);
  if (noteName || noteSlug) {
    return {
      label: formatCampaignLabel(noteName, noteSlug),
      name: noteName,
      slug: noteSlug,
    };
  }

  return null;
}
